import React from "react";
import { useTranslation } from "react-i18next";
import { SRMIcon, Button } from "@picklebet/ui";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { OddsDisplay } from "@/features/betslip/components/parts/OddsDisplay";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import {
  useSrmSuggestion,
  type SrmSuggestion,
} from "@/features/betslip/hooks/useSrmSuggestion";

import * as styles from "@/features/betslip/components/cards/CreateSrmCard.module.scss";

type OpenSuggestion = Exclude<SrmSuggestion, { kind: "none" }>;

// The "Create SRM" prompt: shows once every single in the slip is an eligible
// same-race leg the server can price into an SRM (see getSrmCandidate +
// useSrmSuggestion). It previews the SRM those singles would become — a bordered
// SRM group (tinted band + event header + connected legs) inside the card — with
// the combined price and a one-tap create.
export const CreateSrmCard = () => {
  const suggestion = useSrmSuggestion();
  if (suggestion.kind === "none") return null;
  return <Card suggestion={suggestion} />;
};

const Card = ({ suggestion }: { suggestion: OpenSuggestion }) => {
  const { t } = useTranslation();
  const { legs, odds, createSrm, dismiss } = suggestion;
  const base = legs[0];
  const title = useTitle((base.sport ?? "").toLowerCase());

  return (
    <BetCard testId="create-srm-card">
      <div className={styles.group}>
        <div className={styles.band}>
          <SRMIcon className={styles.srmIcon} />
          <span className={styles.info}>
            {legs.length} {t("Legs")}{" "}
            <span className={styles.at}>
              @{" "}
              {odds != null ? (
                <OddsDisplay odds={odds} />
              ) : (
                <span className={styles.pricing}>…</span>
              )}
            </span>
          </span>
          <Button
            variant="primary"
            size="xxs"
            disabled={odds == null}
            onClick={createSrm}
            data-testid="create-srm-cta"
          >
            Create {t("SRM")}
          </Button>
        </div>

        <div className={styles.content}>
          <BetCardHeader
            icon={<TitleIcon title={title} />}
            title={base.eventName}
            clamp={<SelectionEventStatus selection={base} />}
            rightSlot={<RemoveButton onClick={dismiss} testId="dismiss-srm" />}
          />
          <BetSpine>
            {legs.map((leg) => (
              <BetRow
                key={leg.outcomeId}
                icon={
                  <OutcomeIcon
                    competitor={leg.competitor}
                    silksUrl={leg.silksUrl}
                    size={24}
                    reserveAvatarSpace
                  />
                }
                name={leg.selectionName}
                market={
                  <>
                    {leg.marketName} @ <OddsDisplay odds={leg.odds} />
                  </>
                }
                testId={`srm-preview-leg-${leg.outcomeId}`}
              />
            ))}
          </BetSpine>
        </div>
      </div>
    </BetCard>
  );
};
